/**
 * Round 80 — A-C-12 Governance & Explainability
 *
 * Tiny "Why?" trigger that opens an `ExplanationPopover`. Designed to
 * sit inline next to a metric, header, or card. Renders nothing visible
 * besides a single icon-sized button so it never affects layout.
 */
import { Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { ExplanationPopover } from './ExplanationPopover';
import type { OptiBayExplanation } from '@/lib/intelligence-explanations/types';
import { useEffect, useState } from 'react';
import { logIntelligenceView } from '@/lib/intelligence-explanations/audit';

interface WhyButtonProps {
  /** Lazy builder so we don't compute explanations until the popover opens. */
  build: () => OptiBayExplanation;
  /** Stable identifier for audit (e.g. 'avg_efficiency', artifact_id). */
  entityId?: string;
  entityType?: string;
  scopeType?: string | null;
  scopeId?: string | null;
  /** Optional override for tooltip / aria label. */
  label?: string;
  className?: string;
}

export function WhyButton({
  build,
  entityId,
  entityType = 'metric',
  scopeType,
  scopeId,
  label = 'Why am I seeing this?',
  className,
}: WhyButtonProps) {
  const [open, setOpen] = useState(false);
  const [explanation, setExplanation] = useState<OptiBayExplanation | null>(null);

  useEffect(() => {
    if (open && !explanation) {
      setExplanation(build());
      logIntelligenceView({
        action_type: 'view_explanation',
        entity_type: entityType,
        entity_id: entityId ?? null,
        scope_type: scopeType ?? null,
        scope_id: scopeId ?? null,
      });
    }
  }, [open, explanation, build, entityType, entityId, scopeType, scopeId]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={label}
          className={className ?? 'h-5 w-5 p-0 text-muted-foreground hover:text-foreground'}
        >
          <Info className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-0">
        {explanation && <ExplanationPopover explanation={explanation} />}
      </PopoverContent>
    </Popover>
  );
}
